import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { connect } from '../../../redux/store';
import { I18n } from '@kineticdata/react';
import { PageTitle } from '../../shared/PageTitle';
import { getStatusColor } from '../../shared/content_manager/helpers';
import { connectUser, connectSubteam } from '../../../utils';
import {
  EmptyMessage,
  LoadingMessage,
  PaginationBar,
  C,
} from '@kineticdata/bundle-common';
import { Map, List } from 'immutable';
import classNames from 'classnames';

const UserPart = connectUser(
  ({ user, username, inTeam }) => (
    <span className={classNames('d-block mb-2 mb-nlc', { 'ml-3': inTeam })}>
      {user === false ? (
        <span>
          <I18n>User not found</I18n>: {username}
        </span>
      ) : !user ? (
        <span className="text-muted">
          <span className="fa fa-spinner fa-spin fa-fw" />
          <I18n>Loading</I18n>
        </span>
      ) : (
        <>
          <span className="d-block">{user.displayName}</span>
          {'<'}
          <a href={`mailto:${user.email}`}>{user.email}</a>
          {'>'}
        </>
      )}
    </span>
  ),
  props => props.username,
);

const TeamPart = connectSubteam(
  ({ team, teamName }) => (
    <span className="d-block mb-3 mb-nlc">
      <span className="d-block mb-1">{teamName}</span>
      {team &&
        team.memberships.map(({ user }) => (
          <UserPart
            key={user.username}
            username={user.username}
            inTeam={true}
          />
        ))}
    </span>
  ),
  props => props.teamName,
);

const OwnershipComponent = ({
  // Portoflios
  portfolios,
  portfoliosList,
  portfoliosError,
  portfoliosInit,
  portfoliosLoading,
  portfoliosNameMap,
  // Services
  services,
  servicesList,
  servicesError,
  servicesInit,
  servicesLoading,
  // Other state
  technicalOwnerTeams,
  businessOwnerTeams,
  usersMap,
  profile,
  appLocation,
  isMobile,
  navigate,
}) => {
  // Filter states
  const [includeRevisions, setIncludeRevisions] = useState(true);
  const [type, setType] = useState(null);
  // Paging state
  const [pageSize, setPageSize] = useState(10);
  const [index, setIndex] = useState(0);
  // State for storing if page is about to be printed so we can change the UI.
  const [printView, setPrintView] = useState(false);
  // Handlers for setting the printView state
  const handleBeforePrint = useCallback(() => setPrintView(true), []);
  const handleAfterPrint = useCallback(() => setPrintView(false), []);
  // Event listeners for setting the printView state
  useEffect(() => {
    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  });

  const data = useMemo(
    () => {
      // Get relevant portfolios based on the selected filters
      const relevantPortfolios = (
        ((!type || type === 'portfolios') && portfolios) ||
        Map()
      )
        .map(
          l =>
            includeRevisions
              ? l.first()
              : l.find(p => p.values['Status'] === 'Active'),
        )
        .toList()
        .filter(Boolean)
        // Map to the values we need for the table
        .map(p => ({
          id: p.values[C.FIELD_PORTFOLIO_ID],
          name:
            p.values['Name'] ||
            p.values[C.FIELD_PORTFOLIO_ID] ||
            '*Unnamed Portfolio*',
          type: 'PORTFOLIO',
          status: p.values['Status'],
          owners: p.values['Portfolio Owners'] || [],
          technicalOwners: p.values['Technical Owners'] || [],
          businessOwners: p.values['Business Owners'] || [],
        }));

      // Get relevant services based on the selected filters
      const relevantServices = (
        ((!type || type === 'services') && services) ||
        Map()
      )
        .map(
          l =>
            includeRevisions
              ? l.first()
              : l.find(p => p.values['Status'] === 'Active'),
        )
        .toList()
        .filter(Boolean)
        // Map to the values we need for the table
        .map(p => ({
          id: p.values[C.FIELD_SERVICE_ID],
          name:
            p.values['Name'] ||
            p.values[C.FIELD_SERVICE_ID] ||
            '*Unnamed Service*',
          type: 'SERVICE',
          status: p.values['Status'],
          owners: p.values['Service Owners'] || [],
          technicalOwners: p.values['Technical Owners'] || [],
          businessOwners: p.values['Business Owners'] || [],
        }));

      return relevantPortfolios
        .concat(relevantServices)
        .sortBy(i => i.name.toLowerCase());
    },
    [includeRevisions, type, portfolios, services],
  );

  // Paging handlers
  const count = data.size;
  const changePageSize = useCallback(
    newPageSize => () => setPageSize(newPageSize),
    [setPageSize],
  );
  const previousPage = useCallback(
    () => setIndex(Math.max(index - pageSize, 0)),
    [index, pageSize, setIndex],
  );
  const nextPage = useCallback(
    () => setIndex(index + pageSize < count ? index + pageSize : index),
    [index, pageSize, count, setIndex],
  );
  const goToPage = useCallback(newPage => () => setIndex(newPage * pageSize), [
    pageSize,
    setIndex,
  ]);
  // Data to be shown: either the current page slice, or all data for print view
  const displayData = !printView ? data.slice(index, index + pageSize) : data;

  return (
    <div className="page-container">
      <div className="page-panel">
        <PageTitle
          className="page-title-heading--sm"
          parts={['Catalog Ownership', 'Catalog Management']}
          title="Catalog Ownership"
          breadcrumbs={[
            {
              label: (
                <span>
                  <span className="fa fa-fw fa-md fa-arrow-left text-muted" />
                  <I18n>{C.SETTINGS_LABEL}</I18n>
                </span>
              ),
              to: '/settings',
            },
            { label: 'Catalog Management', to: '..' },
          ]}
        />

        <div className="ownership-filters form p-0 d-print-none">
          <div className="form-group__columns">
            <div className="form-group radio" id="type-radio-group">
              <label htmlFor="type-radio-group">
                <strong>
                  <I18n>Type</I18n>
                </strong>
              </label>
              <label htmlFor="type-all">
                <input
                  type="radio"
                  id="type-all"
                  name="type-radio-group"
                  checked={!type}
                  onChange={() => setType(null)}
                />{' '}
                <I18n>All</I18n>
              </label>
              <label htmlFor="type-portfolios">
                <input
                  type="radio"
                  id="type-portfolios"
                  name="type-radio-group"
                  checked={type === 'portfolios'}
                  onChange={() => setType('portfolios')}
                />{' '}
                <I18n>Portfolios</I18n>
              </label>
              <label htmlFor="type-services">
                <input
                  type="radio"
                  id="type-services"
                  name="type-radio-group"
                  checked={type === 'services'}
                  onChange={() => setType('services')}
                />{' '}
                <I18n>Services</I18n>
              </label>
            </div>
            <div className="form-group checkbox">
              <label htmlFor="include-revisions">
                <input
                  type="checkbox"
                  id="include-revisions"
                  checked={includeRevisions}
                  onChange={() => setIncludeRevisions(checked => !checked)}
                />{' '}
                <I18n>Include Revisions</I18n>
              </label>
            </div>
          </div>
        </div>

        {portfoliosError && (
          <div className="alert alert-danger alert-bar">
            <div>
              <I18n>Failed to retrieve portfolios.</I18n>
            </div>
            <small>{portfoliosError.message}</small>
          </div>
        )}
        {servicesError && (
          <div className="alert alert-danger alert-bar">
            <div>
              <I18n>Failed to retrieve services.</I18n>
            </div>
            <small>{servicesError.message}</small>
          </div>
        )}

        {(!portfoliosError && !portfoliosInit && portfoliosLoading) ||
        (!servicesError && !servicesInit && servicesLoading) ? (
          <LoadingMessage />
        ) : (
          <>
            <table className="table table--settings">
              <thead>
                <tr>
                  <th>
                    <I18n>Catalog Item</I18n>
                  </th>
                  <th>
                    <I18n>Owners</I18n>
                  </th>
                  <th>
                    <I18n>Technical Owners</I18n>
                  </th>
                  <th>
                    <I18n>Business Owners</I18n>
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayData.map(item => (
                  <tr key={item.id}>
                    <td>
                      <small className="d-block text-muted">
                        <I18n>{item.type}</I18n>
                      </small>
                      <span className="d-block fw-600">
                        <I18n>{item.name}</I18n>
                      </span>
                      <span
                        className={`badge badge-${getStatusColor(
                          item.status,
                        )} badge-stylized text-muted mt-2`}
                      >
                        <I18n>{item.status}</I18n>
                      </span>
                    </td>
                    <td>
                      {item.owners.map(owner => (
                        <UserPart key={owner} username={owner} />
                      ))}
                      {item.owners.length === 0 && (
                        <em>
                          <I18n>None</I18n>
                        </em>
                      )}
                    </td>
                    <td>
                      {item.technicalOwners.map(team => (
                        <TeamPart key={team} teamName={team} />
                      ))}
                      {item.technicalOwners.length === 0 && (
                        <em>
                          <I18n>None</I18n>
                        </em>
                      )}
                    </td>
                    <td>
                      {item.businessOwners.map(team => (
                        <TeamPart key={team} teamName={team} />
                      ))}
                      {item.businessOwners.length === 0 && (
                        <em>
                          <I18n>None</I18n>
                        </em>
                      )}
                    </td>
                  </tr>
                ))}
                {count === 0 && (
                  <tr>
                    <td colSpan="4">
                      <EmptyMessage title="No catalog items found." />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {count > 0 && (
              <PaginationBar
                className="mb-3 d-print-none"
                previousPage={index > 0 ? previousPage : undefined}
                nextPage={index + pageSize < count ? nextPage : undefined}
                startIndex={index + 1}
                endIndex={Math.max(
                  Math.min(index + pageSize, count),
                  index + 1,
                )}
                count={count}
                countDone={true}
                pageSize={pageSize}
                changePageSize={changePageSize}
                currentPage={Math.floor(index / pageSize)}
                pageCount={Math.ceil(count / pageSize)}
                goToPage={goToPage}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state, props) => {
  return {
    // Portfolios
    portfolios: state.settingsPortfolio.data,
    portfoliosList: state.settingsPortfolio.dataList,
    portfoliosInit: state.settingsPortfolio.dataInit,
    portfoliosLoading: state.settingsPortfolio.dataLoading,
    portfoliosError: state.settingsPortfolio.dataError,
    portfoliosNameMap: state.settingsPortfolio.dataList.reduce(
      (map, p) => map.set(p.values[C.FIELD_PORTFOLIO_ID], p.values['Name']),
      Map(),
    ),
    // Services
    services: state.settingsService.data,
    servicesList: state.settingsService.dataList,
    servicesInit: state.settingsService.dataInit,
    servicesLoading: state.settingsService.dataLoading,
    servicesError: state.settingsService.dataError,
    // Teams and users
    technicalOwnerTeams: state.app.subteamsMap.get(
      C.CATALOG_TECHNICAL_OWNERS_TEAM_NAME,
      List(),
    ),
    businessOwnerTeams: state.app.subteamsMap.get(
      C.CATALOG_BUSINESS_OWNERS_TEAM_NAME,
      List(),
    ),
    usersMap: state.app.usersMap,
    // Other state
    profile: state.app.profile,
    appLocation: state.app.location,
    layoutSize: state.app.layoutSize,
    isMobile: ['small', 'medium'].includes(state.app.layoutSize),
  };
};

const mapDispatchToProps = {
  // fetchPortfolios: portfolioActions.fetchPortfoliosRequest,
  // fetchServices: serviceActions.fetchServicesRequest,
  // fetchAppendices: appendixActions.fetchAppendicesRequest,
  // createPortfolio: portfolioActions.createPortfolioRequest,
  // createService: serviceActions.createServiceRequest,
  // createAppendix: appendixActions.createAppendixRequest,
};

export const Ownership = connect(
  mapStateToProps,
  mapDispatchToProps,
)(OwnershipComponent);
